import Logger from "common/Logger"; const LOGGER = new Logger("googleAnalytics.ts");export default class GoogleAnalytics {
    private initialized = false
    private measurementId: string | null = null
    private debug: boolean = false
    public trackEvent(category: string, action: string, label?: string, value?: number): void {
        if (!this.isReady()) return // ← checks this.initialized which IS reset by onerror
        window.gtag("event", action, {
            event_category: category,
            event_label: label,
            value: value
        })
    }

    public init(measurementId: string, debug: boolean = false, onError?: () => void): void {
        if (typeof window === "undefined" || typeof document === "undefined") {
            LOGGER.warn("Google Analytics skipped — not a browser environment", { window: typeof window, document: typeof document })
            return
        }

        if (this.initialized || !measurementId) {
            LOGGER.warn("GA already initialized or no measurement ID", {
                initialized: this.initialized,
                measurementId: !!measurementId
            })

            return
        }

        this.debug = debug
        this.measurementId = measurementId

        if (this.debug) LOGGER.info(" GA Init - Measurement ID:", measurementId)

        const script = document.createElement("script")
        script.async = true
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`

        script.onerror = (error) => {
            LOGGER.error("GA Script failed to load", {
                error: error instanceof Error ? error.message : String(error),
                src: script.src
            })

            this.initialized = false // prevent isReady() returning true
            onError?.()
        }

        document.head.appendChild(script)

        // Initialize dataLayer and gtag
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        window.dataLayer = window.dataLayer || []

        // Capture debug flag for the closure
        const isDebug = this.debug
        // IMPORTANT: Must remain a regular function() — NOT an arrow function or rest params (...args).
        // GTM's dataLayer queue processing requires an IArguments object. Converting to an array
        // (via rest params or Array.from) breaks GA4 event transmission silently.
        // See: https://developers.google.com/tag-platform/tag-manager/datalayer

        const gtag: any = function () {
            // eslint-disable-next-line prefer-rest-params
            if (isDebug) LOGGER.info("GA Call", { args: Array.from(arguments) })
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments)
        }

        window.gtag = gtag

        gtag("js", new Date())
        gtag("config", measurementId, {
            send_page_view: false,
            debug_mode: debug // Enable GA4 debug mode
        })

        this.initialized = true

        if (this.debug) LOGGER.info("Google Analytics initialized", {})
    }

    public pageView(pagePath: string, pageTitle?: string): void {
        if (!this.isReady()) return

        const params = {
            page_title: pageTitle || document.title,
            page_location: window.location.href,
            page_path: pagePath
        }

        window.gtag("event", "page_view", params)
    }

    public event(eventName: string, params?: Record<string, any>): void {
        if (!this.isReady()) return

        window.gtag("event", eventName, params)
    }

    public setUser(userId: string): void {
        if (!this.isReady() || !this.measurementId) return

        window.gtag("config", this.measurementId, {
            user_id: userId
        })
    }

    public setUserProperties(properties: Record<string, any>): void {
        if (!this.isReady()) return

        window.gtag("set", "user_properties", properties)
    }

    public exception(description: string, fatal: boolean = false): void {
        if (!this.isReady()) return

        window.gtag("event", "exception", {
            description,
            fatal
        })
    }

    public setEnabled(enabled: boolean): void {
        if (!this.measurementId) return

        window[`ga-disable-${this.measurementId}`] = !enabled
    }

    private isReady(): boolean {
        if (typeof window === "undefined") return false
        if (!this.initialized) {
            LOGGER.warn("Google Analytics not initialized", this.initialized)
            return false
        }

        return true
    }
}

declare global {
    interface Window {
        dataLayer: any[]
        gtag: (...args: any[]) => void
        [key: string]: any // For ga-disable-GA_MEASUREMENT_ID
    }
}
